import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Clock, Film, CheckCircle2, Crown, Video, MessageSquare, BarChart3, Newspaper, ClipboardCheck, Truck, Archive, Clapperboard, Trophy, ExternalLink, Coins, Megaphone, CalendarHeart, Search, LogOut, Gem, HelpCircle, BadgeCheck, Phone, MapPin, Copy, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { StatusBadge } from "@/components/dashboard-parts";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import GroupForum from "@/components/GroupForum";
import ReportPanel from "@/components/ReportPanel";
import NewsPanel from "@/components/NewsPanel";
import CalendarPanel from "@/components/CalendarPanel";
import ArchivePanel from "@/components/ArchivePanel";
import VideoblogPanel from "@/components/VideoblogPanel";
import OngkirPanel from "@/components/OngkirPanel";
import AwardsPanel from "@/components/AwardsPanel";
import LeaderboardPanel from "@/components/LeaderboardPanel";
import PointsAdminPanel from "@/components/PointsAdminPanel";
import CampaignAdminPanel from "@/components/CampaignAdminPanel";
import HelpPanel from "@/components/HelpPanel";
import VerificationsPanel from "@/components/VerificationsPanel";
import ExcelDashboardPanel from "@/components/ExcelDashboardPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { id: "campaign", label: "Campaign", icon: Megaphone },
  { id: "approvals", label: "Video Approval", icon: ClipboardCheck },
  { id: "report", label: "Report", icon: BarChart3 },
  { id: "points", label: "Point & Tier", icon: Coins },
  { id: "verifications", label: "Verifikasi Akun", icon: BadgeCheck },
  { id: "excel", label: "Dashboard Excel", icon: FileSpreadsheet },
  { id: "news", label: "News", icon: Newspaper },
  { id: "calendar", label: "Calendar", icon: CalendarHeart },
  { id: "archive", label: "Arsip", icon: Archive },
  { id: "videoblog", label: "Videoblog", icon: Clapperboard },
  { id: "ongkir", label: "Ongkir KOL", icon: Truck },
  { id: "awards", label: "Awards", icon: Trophy },
  { id: "leaderboard", label: "Papan Juara", icon: Crown },
  { id: "forum", label: "Forum", icon: MessageSquare },
  { id: "help", label: "Bantuan / FAQ", icon: HelpCircle },
];

const embedVideoUrl = (url) => {
  const u = url || "";
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const gd = u.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  if (u.includes("dropbox.com")) { const base = u.replace(/[?&]dl=\d/, ""); return base + (base.includes("?") ? "&" : "?") + "raw=1"; }
  return u;
};

export default function AdminDashboard({ demo }) {
  const { user, logout } = useAuth();
  const isSecretariat = (user?.email || "").toLowerCase() === "secretariatshowty@gmail.com";
  const [active, setActive] = useState("campaign");
  const [stats, setStats] = useState(demo ? { creators: 128, affiliators: 84, campaigns: 12, pending_campaign_proofs: 7, pending_submissions: 5, approved_submissions: 63 } : null);
  const [roleFilter, setRoleFilter] = useState("beauty_creator");
  const [submissions, setSubmissions] = useState([]);

  const loadStats = useCallback(() => { if (demo) return; api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, [demo]);
  const loadData = useCallback(async (role) => { if (demo) return; try { setSubmissions((await api.get(`/submissions?role_filter=${role}`)).data); } catch {} }, [demo]);
  const exportSubmissions = async () => {
    try {
      const res = await api.get(`/submissions/export?role_filter=${roleFilter}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "showtynion-video-submission.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export Excel"); }
  };
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadData(roleFilter); }, [roleFilter, loadData]);

  const review = async (kind, id, status) => {
    try {
      await api.patch(`/${kind}/${id}/status`, { status });
      toast.success(status === "rejected" ? "Ditolak" : status === "approved" ? "Final approved (CEO)" : "Disetujui manager — menunggu CEO");
      loadData(roleFilter); loadStats();
    } catch (e) { toast.error("Gagal memproses"); }
  };

  const displayName = user?.name || (demo ? "Manager Showty" : "Manager");
  const current = NAV.find((n) => n.id === active) || NAV[0];

  const STATS = [
    { icon: Crown, label: "Creators", value: stats?.creators, tone: "text-showty-red" },
    { icon: Video, label: "Affiliators", value: stats?.affiliators, tone: "text-showty-navy" },
    { icon: Megaphone, label: "Campaign", value: stats?.campaigns ?? 0, tone: "text-showty-red" },
    { icon: Clock, label: "Bukti pending", value: stats?.pending_campaign_proofs ?? 0, tone: "text-showty-gold" },
    { icon: Film, label: "Video pending", value: stats?.pending_submissions, tone: "text-showty-gold" },
    { icon: CheckCircle2, label: "Video approved", value: stats?.approved_submissions, tone: "text-green-600" },
  ];

  return (
    <div className="min-h-screen bg-showty-cream showty-grain flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#fdf6ec] border-r border-border sticky top-0 h-screen" data-testid="manager-sidebar">
        <div className="px-6 py-6 flex items-center gap-2.5">
          <Logo className="w-9 h-9" />
          <div><p className="font-display text-2xl leading-none text-showty-navy">Showtynion</p><p className="text-[10px] tracking-[0.2em] uppercase text-showty-red mt-1">KOL Manager</p></div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setActive(n.id)} data-testid={`section-${n.id}`}
              className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${active === n.id ? "bg-showty-navy text-white shadow-sm" : "text-showty-navy/80 hover:bg-showty-pink/40"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <p className="px-6 py-5 font-display text-lg text-showty-red/70 leading-tight italic">Where beauty<br />meet community</p>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-showty-cream/85 backdrop-blur-xl border-b border-border px-4 sm:px-8 py-3.5 flex items-center gap-3">
          <div className="relative flex-1 max-w-2xl">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input placeholder="Cari campaign, KOL, report, atau berita..." data-testid="manager-search" className="w-full h-11 rounded-full bg-white border border-border pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-showty-navy/30" />
          </div>
          {!demo && <NotificationBell />}
          <div className="flex items-center gap-2.5 pl-1">
            {user?.picture ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-border" /> : <div className="w-10 h-10 rounded-full bg-showty-navy flex items-center justify-center text-white font-semibold">{displayName[0]}</div>}
            <div className="hidden sm:block leading-tight"><p className="text-sm font-semibold text-showty-navy">{displayName}</p><p className="text-xs text-muted-foreground">{isSecretariat ? "Secretariat / CEO" : "Manager"}</p></div>
            <button onClick={logout} className="text-muted-foreground hover:text-showty-red" data-testid="logout-btn"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>

        <div className="lg:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-showty-cream/90">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setActive(n.id)} className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${active === n.id ? "bg-showty-navy text-white" : "text-showty-navy/80 bg-white border border-border"}`}>
              <n.icon className="w-3.5 h-3.5" /> {n.label}
            </button>
          ))}
        </div>

        <main className="flex-1 px-4 sm:px-8 py-6 max-w-[1400px] w-full">
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-7" data-testid="manager-stats">
              {STATS.map((s) => (
                <div key={s.label} className="rounded-2xl border border-border bg-white p-4" data-testid={`stat-${s.label}`}>
                  <s.icon className={`w-5 h-5 ${s.tone}`} />
                  <p className="mt-2 font-display text-2xl text-showty-navy">{s.value ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mb-5"><h1 className="font-display text-3xl sm:text-4xl text-showty-navy flex items-center gap-2.5"><current.icon className="w-7 h-7 text-showty-red" /> {current.label}</h1></div>

          {active === "campaign" && <CampaignAdminPanel onChange={loadStats} />}
          {active === "verifications" && <VerificationsPanel />}
          {active === "excel" && <ExcelDashboardPanel />}
          {active === "report" && <ReportPanel />}
          {active === "points" && <PointsAdminPanel />}
          {active === "news" && <NewsPanel />}
          {active === "calendar" && <CalendarPanel editable />}
          {active === "archive" && <ArchivePanel />}
          {active === "videoblog" && <VideoblogPanel editable />}
          {active === "ongkir" && <OngkirPanel />}
          {active === "awards" && <AwardsPanel editable />}
          {active === "leaderboard" && <LeaderboardPanel editable />}
          {active === "help" && <HelpPanel />}

          {active === "forum" && (
            <>
              <p className="text-sm text-muted-foreground mb-4">Dua forum komunitas terpisah — KOL dan Affiliator tidak digabung. Kamu (admin) bisa balas di keduanya.</p>
              <Tabs defaultValue="beauty_creator">
                <TabsList className="bg-showty-secondary mb-4">
                  <TabsTrigger value="beauty_creator" data-testid="forum-tab-kol"><Crown className="w-4 h-4 mr-1.5" /> Forum KOL</TabsTrigger>
                  <TabsTrigger value="affiliator" data-testid="forum-tab-aff"><Video className="w-4 h-4 mr-1.5" /> Forum Affiliator</TabsTrigger>
                </TabsList>
                <TabsContent value="beauty_creator"><GroupForum channel="beauty_creator" /></TabsContent>
                <TabsContent value="affiliator"><GroupForum channel="affiliator" /></TabsContent>
              </Tabs>
            </>
          )}

          {active === "approvals" && (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="role-filter">
                  {[{ id: "beauty_creator", label: "Beauty Creators", icon: Crown }, { id: "affiliator", label: "Affiliators", icon: Video }].map((r) => (
                    <button key={r.id} onClick={() => setRoleFilter(r.id)} data-testid={`filter-${r.id}`}
                      className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${roleFilter === r.id ? "bg-showty-navy text-white" : "text-muted-foreground hover:text-showty-navy"}`}>
                      <r.icon className="w-4 h-4" /> {r.label}
                    </button>
                  ))}
                </div>
                {!demo && <Button onClick={exportSubmissions} variant="outline" className="rounded-full ml-auto" data-testid="submissions-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel</Button>}
              </div>
              <div className="space-y-3">
                {submissions.length === 0 ? <Empty text="Tidak ada submission video" /> : submissions.map((s) => (
                  <div key={s.id} className="rounded-xl border border-border bg-white p-4" data-testid={`admin-sub-${s.id}`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex-1 min-w-[180px]">
                        <p className="font-medium text-showty-navy">{s.product_name}</p>
                        <p className="text-xs text-muted-foreground">{s.user_name} · {s.platform || "—"}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                          {s.whatsapp ? (
                            <a href={`https://wa.me/${s.whatsapp.replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent("Halo " + (s.user_name || "") + ", terkait video campaign \"" + s.product_name + "\" yang kamu submit…")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white px-2.5 py-1 font-semibold hover:bg-green-700" data-testid={`sub-wa-${s.id}`}>
                              <Phone className="w-3 h-3" /> Chat WA
                            </a>
                          ) : <span className="text-muted-foreground italic">No WA belum diisi</span>}
                          {s.city && <span className="inline-flex items-center gap-1 text-showty-navy"><MapPin className="w-3 h-3 text-showty-red" /> {s.city}</span>}
                          {s.video_links?.[0] && (
                            <button onClick={() => { navigator.clipboard?.writeText(s.video_links[0]); toast.success("Link video disalin"); }} className="inline-flex items-center gap-1 rounded-full bg-showty-cream text-showty-navy px-2.5 py-1 hover:bg-showty-pink/40" data-testid={`sub-copy-${s.id}`}>
                              <Copy className="w-3 h-3" /> Salin link video
                            </button>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={s.status} />
                      <StageActions status={s.status} isSecretariat={isSecretariat} kind="submissions" id={s.id} onAction={review} />
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      {s.video_files?.map((v, i) => (
                        <div key={`f${i}`} className="rounded-xl overflow-hidden border border-border" data-testid={`sub-file-${s.id}-${i}`}>
                          <video controls className="w-full aspect-video bg-black" src={`${API}/media/${v.path}`} />
                          <a href={`${API}/submissions/${s.id}/video/${i}/download`} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-showty-navy hover:bg-showty-navy/90" data-testid={`sub-video-download-${s.id}-${i}`}><Download className="w-3 h-3" /> Download HD: {v.name}</a>
                        </div>
                      ))}
                      {s.video_links?.map((l, i) => (
                        <div key={`l${i}`} className="rounded-xl overflow-hidden border border-border" data-testid={`sub-preview-${s.id}-${i}`}>
                          <div className="aspect-video w-full bg-black"><iframe title={`preview-${s.id}-${i}`} src={embedVideoUrl(l)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen /></div>
                          <a href={l} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 text-xs text-showty-red hover:underline" data-testid={`sub-link-${s.id}-${i}`}><ExternalLink className="w-3 h-3" /> Buka Link {i + 1}</a>
                        </div>
                      ))}
                      {(!s.video_files?.length && !s.video_links?.length) && <span className="text-xs text-muted-foreground">Tidak ada media</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const StageActions = ({ status, isSecretariat, kind, id, onAction }) => {
  if (status === "pending") return (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onAction(kind, id, "manager_approved")} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${id}`}>Approve (Manager)</Button>
      <Button size="sm" variant="outline" onClick={() => onAction(kind, id, "rejected")} className="rounded-full border-destructive text-destructive hover:bg-destructive/10" data-testid={`reject-${id}`}>Reject</Button>
    </div>
  );
  if (status === "manager_approved") return isSecretariat ? (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onAction(kind, id, "approved")} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid={`final-approve-${id}`}>Final Approve (CEO)</Button>
      <Button size="sm" variant="outline" onClick={() => onAction(kind, id, "rejected")} className="rounded-full border-destructive text-destructive hover:bg-destructive/10" data-testid={`reject-${id}`}>Reject</Button>
    </div>
  ) : <span className="text-xs text-muted-foreground italic" data-testid={`await-ceo-${id}`}>Menunggu final approval CEO</span>;
  return null;
};

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white/50 py-14 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>
);
